import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  LinearProgress,
  linearProgressClasses,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { CollectionSectionHeader } from './CollectionSectionHeader';
type Props = {
  bagUploadsCount: number;
  alleysCount: number;
  uploadProgress: number;
  progressStatus: 'primary' | 'success' | 'warning' | 'error';
};

export const CollectionUploadProgress = ({
  bagUploadsCount,
  alleysCount,
  uploadProgress,
  progressStatus,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Custom progress bar color map
  const progressStatusMap = {
    primary: '#0090F0',
    success: theme.vars.palette.success.main,
    warning: theme.vars.palette.warning.main,
    error: theme.vars.palette.error.main,
  };

  // Get the appropriate bar color based on the progressStatus prop
  const barColor = progressStatusMap[progressStatus];

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1 }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <CollectionSectionHeader
          icon={
            <CloudUploadIcon
              sx={(theme) => ({ color: theme.vars.palette.text.secondary })}
            />
          }
          title={t('Upload Progress')}
        />
        <Typography variant='body2'>
          {bagUploadsCount} / {alleysCount} {t('bags')}
        </Typography>
      </Stack>

      <LinearProgress
        variant='determinate'
        value={uploadProgress}
        color='primary'
        sx={{
          height: 10,
          borderRadius: 4,
          mt: 3,
          mb: 2,
          [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.vars.palette.common.black,
          },
          [`& .${linearProgressClasses.bar}`]: {
            backgroundColor: barColor,
          },
        }}
      />

      {bagUploadsCount > alleysCount && (
        <Alert severity='warning' sx={{ mt: 1 }}>
          {t(
            'More bags ({{bags}}) than alleys ({{alleys}}) detected. This may indicate duplicate uploads.',
            {
              bags: bagUploadsCount,
              alleys: alleysCount,
            }
          )}
        </Alert>
      )}

      {alleysCount > 0 && bagUploadsCount < alleysCount && (
        <Alert severity='info' sx={{ mt: 1 }}>
          {t(
            'Upload in progress. {{remaining}} remaining bags to be uploaded.',
            {
              remaining: alleysCount - bagUploadsCount,
            }
          )}
        </Alert>
      )}
    </Paper>
  );
};
